// Use the exact same chevron as the react select dropdown.
import React from "react";

import { faTag } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMediaQuery } from "@material-ui/core";
import Downshift, { ControllerStateAndHelpers } from "downshift";
import { useField } from "formik";
import { components } from "react-select";
import styled from "styled-components/macro";

import { Chip } from "components/Chip";
import { Button } from "components/Form/Button";
import {
  StyledCheckbox,
  Error,
  Label,
  SelectFieldOption,
  LabelSelectFieldOption,
} from "components/Form/FormikControls";
import { Column, Row } from "components/Layout";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import { useTheme } from "hooks";

import SearchInput from "components/Form/SearchInput";
import AgriNousActiveLogo from "img/AgriNousActiveLogo";
import { Expander, SelectInput } from "./Downshift";

const FaIcon = styled(FontAwesomeIcon)`
  font-size: 18px;
  margin: 0px ${({ theme }) => theme.space[1]}px;
`;

const Item = styled.li`
  cursor: pointer;
  display: block;
  border: none;
  font-size: 14px;
  text-transform: none;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHighlight}50;
  }
`;

const stateReducer = (state, changes) => {
  switch (changes.type) {
    case Downshift.stateChangeTypes.keyDownEnter:
    case Downshift.stateChangeTypes.clickItem:
      return {
        ...changes,
        highlightedIndex: state.highlightedIndex,
        isOpen: true,
        inputValue: "",
      };

    default:
      return changes;
  }
};

interface SelectedOptionProps {
  item: string;
  options: SelectFieldOption[];
}

const SelectedOption = ({ item, options }: SelectedOptionProps) => {
  const selectedLabel = options.find(o => o.value === item)?.label || "";

  return (
    <Chip>
      <strong>{selectedLabel}</strong>
    </Chip>
  );
};

interface LabelSelectorFieldProps {
  label: string;
  name: string;
  tooltip?: string;
  required?: boolean;
  disabled?: boolean;
  options: LabelSelectFieldOption[];
  addLabel?: (label: string) => void;
  placeHolder?: string;
}

interface MultiSelectCrollerAndHelpers<T>
  extends Omit<ControllerStateAndHelpers<T>, "selectedItem"> {
  selectedItem: string[]; // we are passing selected item as an array which is a suggested pattern but not valid according to DownShifts default typing
}

export const LabelSelectorField = ({
  label,
  name,
  tooltip,
  required = false,
  disabled,
  options,
  addLabel,
  placeHolder = "Click to add descriptive labels",
}: LabelSelectorFieldProps) => {
  const [filterText, setFilterText] = React.useState("");
  const [field, meta, helpers] = useField(name);
  const error = meta.touched && meta.error;
  const selectedOptions = field.value || [];

  const theme = useTheme();

  const handleChange = item => {
    if (selectedOptions.includes(item)) {
      helpers.setValue(selectedOptions.filter(r => r !== item));
    } else {
      helpers.setValue([...selectedOptions, item]);
    }
    helpers.setTouched(true);
  };

  const fullScreen = useMediaQuery(`(max-width: ${theme.breakpoints[1]}px)`);
  const filteredOptions = options.filter(o =>
    o.label.toLowerCase().includes(filterText.toLowerCase()),
  );

  return (
    <Column fullWidth>
      <Label
        htmlFor={name}
        error={!!error}
        required={required}
        tooltip={tooltip}
      >
        {label}
      </Label>
      <Downshift
        selectedItem={selectedOptions}
        onChange={handleChange}
        stateReducer={stateReducer}
        itemToString={item => item}
      >
        {({
          getToggleButtonProps,
          getMenuProps,
          isOpen,
          selectedItem,
          getItemProps,
          toggleMenu,
        }: MultiSelectCrollerAndHelpers<string>) => {
          const mapSelectedOptions = item => (
            <SelectedOption options={options} key={item} item={item} />
          );

          const menuRef = getMenuProps({}, { suppressRefError: true }).ref;

          return (
            <div>
              <SelectInput onClick={toggleMenu} disabled={disabled}>
                <Row flexWrap paddingHorizontal={1}>
                  {selectedItem.length > 0
                    ? selectedItem.map(mapSelectedOptions)
                    : placeHolder}
                </Row>
                <Expander
                  {...getToggleButtonProps({
                    // prevents the menu from immediately toggling
                    // closed (due to our custom click handler above).
                    onClick(event) {
                      event.stopPropagation();
                    },
                  })}
                >
                  <components.DownChevron />
                </Expander>
              </SelectInput>
              {!disabled && isOpen && (
                <Dialog
                  open
                  maxWidth="sm"
                  fullWidth
                  fullScreen={fullScreen}
                  ref={menuRef}
                >
                  <DialogTitle onClose={toggleMenu}>Labels</DialogTitle>
                  <DialogContent dividers>
                    <SearchInput
                      value={filterText}
                      onChange={setFilterText}
                      selectOnFocus={false}
                    />

                    {filteredOptions.map((option, index) => {
                      const isSelected = selectedOptions.includes(option.value);
                      return (
                        <Item
                          key={option.value}
                          {...getItemProps({
                            item: option.value,
                            index,
                            isSelected,
                          })}
                        >
                          <Row data-tour={option.label} alignCenter>
                            <Column>
                              {option.isMasterLabel ? (
                                <AgriNousActiveLogo
                                  isActive
                                  scale={0.4}
                                  size={20}
                                />
                              ) : (
                                <FaIcon icon={faTag} />
                              )}
                            </Column>
                            <Column flexGrow>{option.label}</Column>
                            <Column>
                              <StyledCheckbox checked={isSelected} />
                            </Column>
                          </Row>
                        </Item>
                      );
                    })}

                    {/* If there a filter active and no exact match, show a button to add a new filter. */}
                    {filterText &&
                      !options.find(o => o.label === filterText) &&
                      addLabel && (
                        <Row justifyCenter alignCenter>
                          <Button
                            data-tour="createNewLabel"
                            onClick={() => addLabel(filterText)}
                          >
                            Create new label
                          </Button>
                        </Row>
                      )}
                  </DialogContent>
                  <DialogActions>
                    <Button data-tour="done" onClick={toggleMenu}>
                      Done
                    </Button>
                  </DialogActions>
                </Dialog>
              )}
            </div>
          );
        }}
      </Downshift>

      {error && <Error>{error}</Error>}
    </Column>
  );
};
